import React from 'react';
import Board from "./Board";
import Menu from "./Menu";
import Panel from "./Panel";
import patterns from "../resources/patterns";

export const BOARD_ROWS = 15;
export const BOARD_COLUMNS = 15;

class Game extends React.Component {
    constructor(props) {
        super(props);
        const boardRef = React.createRef();
        const panelRef = React.createRef();
        this.state = {
            history: [{
                squares: this.fill2dArray(" "),
            }],
            stepNumber: 0,
            isAcross: true,
            symmetrical: true,
            panelControl: "Words",
            boardRef: boardRef,
            panelRef: panelRef,
            focusIndex: null,
            focusRow: null,
            focusCol: null
        }
    }

    transposeArray(matrix) {
        // https://stackoverflow.com/questions/4492678/swap-rows-with-columns-transposition-of-a-matrix-in-javascript#comment24307346_13241545
        return matrix[0].map((col, i) => matrix.map(row => row[i]));
    }

    fill2dArray(fill, transpose=false) {
        let num_rows, num_cols;
        if (transpose) {
            num_rows = BOARD_COLUMNS;
            num_cols = BOARD_ROWS;
        } else {
            num_rows = BOARD_ROWS;
            num_cols = BOARD_COLUMNS;
        }
        let array = []
        for (let i=0; i<num_rows; i++) {
            array.push(Array(num_cols).fill(fill))
        }
        return array
    }

    getCurrentSquares() {
        const history = this.state.history;
        const current = history[history.length - 1];
        return JSON.parse(JSON.stringify(current.squares.slice())); // creates a deep copy
    }

    /**
     * Adds square array to the history list, making it the current state of the game
     * @param {string[][]} squares
     */
    addHistory(squares) {
        const history = this.state.history;
        this.setState({
            history: history.concat([{
                squares: squares,
            }]),
            stepNumber: history.length,
        });
    }

    /**
     * Focuses square at index i, updates state
     * @param {int} i
     */
    focusSquare(i) {
        if (i<BOARD_ROWS*BOARD_COLUMNS && i>=0) {
            let panelContents = this.state.panelRef.current.children[0];
            let boxes = panelContents.querySelectorAll(".suggestion-box")
            if (boxes.length) {
                boxes[0].scrollTo(0,0);
                boxes[1].scrollTo(0,0);  // scroll suggestion lists to the top
            }

            const field = this.state.boardRef.current.state.squareRefs[i].current;
            field.focus();
            this.setState({
                focusIndex: i,
                focusRow: Math.floor(i/BOARD_COLUMNS),
                focusCol: i % BOARD_COLUMNS
            })
        }
    }

    getLabelListHelper(squares, transposed) {
        let labels = this.fill2dArray("", transposed);

        let num_rows, num_columns;
        if (transposed) {
            num_rows = BOARD_COLUMNS;
            num_columns = BOARD_ROWS;
            squares = this.transposeArray(squares);
        } else {
            num_rows = BOARD_ROWS;
            num_columns = BOARD_COLUMNS;
        }

        for (let i = 0; i<num_rows; i++) {
            let prevBlack = true;
            for (let j = 0; j<num_columns; j++) {
                const letter = squares[i][j];
                if (letter === '.') {
                    prevBlack = true;
                } else if (prevBlack) {
                    labels[i][j]='L';
                    prevBlack = false;
                }
            }
        }

        return labels;
    }

    /**
     * Generates a 2d array of labels for current squares, returns array of number strings, empty string if no label
     * @returns labels {string[][]}
     */
    getLabelList() {
        const squares = this.getCurrentSquares();
        const acrossLabels = this.getLabelListHelper(squares, false);
        const downLabels = this.transposeArray(this.getLabelListHelper(squares, true));
        let labels = acrossLabels.slice();

        let label = 1;
        for (let i =0; i<BOARD_ROWS; i++) {
            for (let j=0; j<BOARD_COLUMNS; j++) {
                if (downLabels[i][j]==='L' || acrossLabels[i][j]==='L') {
                    labels[i][j]=label.toString();
                    label = label + 1;
                }
            }
        }

        return labels;
    }

    getWordListHelper(squares) {
        const acrossWords = [];
        for (let row of squares) {
            let word = "";
            for (let letter of row) {
                if (letter !== ".") {
                    word = word + letter;
                } else if (word) {
                    acrossWords.push(word);
                    word = "";
                }
            }
            if (word) {
                acrossWords.push(word);
                word = "";
            }
        }
        return (acrossWords);
    }

    /**
     * Generates a list of all of the words, across and down from a given grid
     * @returns words {string[]}
     */
    getWordList() {
        const squares = this.getCurrentSquares();
        const transpose_squares = this.transposeArray(squares);
        return this.getWordListHelper(squares).concat(this.getWordListHelper(transpose_squares));
    }

    /**
     * Returns index value for moving forward one square, or the same square if nowhere to move forward
     * @param currentIndex {int}
     * @returns {int}
     */
    getForwardIndex(currentIndex) {
        if (this.state.isAcross && currentIndex%BOARD_COLUMNS!==BOARD_COLUMNS-1) {
            return currentIndex+1;
        } else if (!this.state.isAcross && currentIndex<BOARD_ROWS*BOARD_COLUMNS-BOARD_COLUMNS) {
            return currentIndex + BOARD_COLUMNS;
        } else {
            return currentIndex;
        }
    }

    /**
     * Returns index value for moving backward one square, or the same square if nowhere to move backward
     * @param currentIndex {int}
     * @returns {int}
     */
    getBackwardIndex(currentIndex) {
        if (this.state.isAcross && currentIndex%BOARD_COLUMNS!==0) {
           return currentIndex-1;
        } else if (!this.state.isAcross && currentIndex>=BOARD_COLUMNS) {
            return currentIndex-BOARD_COLUMNS;
        } else {
            return currentIndex;
        }
    }

    /**
     * Returns the [row, column] of the start of the word containing letter at index, across or down depending on isAcross value
     * @param index {int}
     * @param squares {string[][]}
     * @param isAcross {boolean}
     * @returns {int[]}
     */
    getStartPosition(index, squares, isAcross) {
        let row = Math.floor(index/BOARD_COLUMNS);
        let column = index%BOARD_COLUMNS;

        if (squares[row][column]===".") return [row, column];

        if (isAcross) {
            while (column%BOARD_COLUMNS !== 0 && squares[row][column-1] !== ".") {
                column = column - 1
            }
            return [row, column]
        } else {
            let trans_column = Math.floor(index/BOARD_COLUMNS);
            let trans_row = index%BOARD_COLUMNS;
            squares = this.transposeArray(squares);
            while (trans_column%BOARD_ROWS !== 0 && squares[trans_row][trans_column-1] !== ".") {
                trans_column = trans_column - 1
            }
            return [trans_column, trans_row]
        }
    }

    /**
     * Returns the [row, column] of the end of the word containing letter at index, across or down depending on isAcross value
     * @param index {int}
     * @param squares {string[][]}
     * @param isAcross {boolean}
     * @returns {int[]}
     */
    getEndPosition(index, squares, isAcross) {
        let row = Math.floor(index/BOARD_COLUMNS);
        let column = index%BOARD_COLUMNS;

        if (squares[row][column]===".") return [row, column];

        if (isAcross) {
            while (column%BOARD_COLUMNS !== BOARD_COLUMNS-1 && squares[row][column+1] !== ".") {
                column = column + 1
            }
            return [row, column]
        } else {
            let trans_column = Math.floor(index/BOARD_COLUMNS);
            let trans_row = index%BOARD_COLUMNS;
            squares = this.transposeArray(squares);
            while (trans_column%BOARD_ROWS !== BOARD_ROWS-1 && squares[trans_row][trans_column+1] !== ".") {
                trans_column = trans_column + 1
            }
            return [trans_column, trans_row]
        }
    }

    getCurrentWordsHelper(row, column, squares, transpose) {
        let max_columns;
        if (transpose) {
            squares = this.transposeArray(squares);
            max_columns = BOARD_ROWS;
        } else {
            max_columns = BOARD_COLUMNS;
        }

        if (squares[row][column]===".") return "";

        while (column%max_columns !== 0 && squares[row][column-1] !== ".") {
            column = column - 1
        }

        let word = "";
        while (column%max_columns !== max_columns-1 && squares[row][column+1] !== ".") {
            word = word + squares[row][column];
            column = column + 1;
        }
        word = word + squares[row][column]

        return word;
    }

    /**
     * Returns [acrossWord, downWord] that both cfontain the square at index
     * @param index {int|null}
     * @returns {string[]}
     */
    getCurrentWords(index) {
        if (index===null) return ["",""]

        const squares = this.getCurrentSquares();
        const row = Math.floor(index/BOARD_COLUMNS);
        const column = index % BOARD_COLUMNS;
        const acrossWord = this.getCurrentWordsHelper(row, column, squares, false);
        const downWord = this.getCurrentWordsHelper(column, row, squares, true);
        return [acrossWord, downWord];
    }

    /**
     * Returns [acrossLabel, downLabel] of the words that intersect square at index
     * @param index {int|null}
     * @returns {string[]}
     */
    getCurrentWordLabels(index) {
        if (index === null) return ["",""]
        const squares = this.getCurrentSquares();
        const labels = this.getLabelList();
        const acrossStart = this.getStartPosition(index, squares, true);
        const downStart = this.getStartPosition(index, squares, false);
        const acrossLabel = labels[acrossStart[0]][acrossStart[1]]
        const downLabel = labels[downStart[0]][downStart[1]]
        return [acrossLabel, downLabel]
    }

    /**
     * Returns a new grid that will toggle the black square at index, and its mirror if symmetry is on
     * @param index {int}
     * @param squares {string[][]}
     * @returns {string[][]}
     */
    toggleBlackSquare(index, squares) {
        const row = Math.floor(index/BOARD_COLUMNS);
        const column = index % BOARD_COLUMNS;
        squares[row][column] === '.' ? squares[row][column] = ' ' : squares[row][column] = '.';  // toggle input square

        if (this.state.symmetrical) {
            squares[BOARD_ROWS-row-1][BOARD_COLUMNS-column-1] = squares[row][column]
        }

        return squares
    }

    /**
     * Function for handling arrow key presses
     * @param {string} input
     * @param {int} index
     */
    handleArrowKey(input, index) {
        if (this.state.isAcross) {
            switch (input) {
                case "ArrowLeft":
                    this.focusSquare(this.getBackwardIndex(index));
                    break;
                case "ArrowRight":
                    this.focusSquare(this.getForwardIndex(index));
                    break;
                default:
                    this.setState({isAcross: false})
            }
        } else {
            switch (input) {
                case "ArrowUp":
                    this.focusSquare(this.getBackwardIndex(index));
                    break;
                case "ArrowDown":
                    this.focusSquare(this.getForwardIndex(index));
                    break;
                default:
                    this.setState({isAcross: true})
            }

        }
    }

    /**
     * Function for handling keyboard presses
     * @param e
     */
    handleKeyDown = (e) => {
        const input = e.key;
        const index = parseInt(e.target.id);
        const row = Math.floor(index/BOARD_COLUMNS);
        const column = index % BOARD_COLUMNS;
        let squares = this.getCurrentSquares();

        if ((input.toUpperCase() !== input.toLowerCase() && input.length === 1 )) { // if letter
            if (squares[row][column] === '.') {
                squares = this.toggleBlackSquare(index, squares)
            }
            squares[row][column] = input.toUpperCase();
            this.addHistory(squares);
            this.focusSquare(this.getForwardIndex(index));

        } else if (input === '.') {
            const new_squares = this.toggleBlackSquare(index, squares);
            this.addHistory(new_squares);

        } else if (input === 'Backspace') {
            e.preventDefault();
            if (squares[row][column] === " ") {
                const backwardIndex = this.getBackwardIndex(index);
                const backwardLetter = squares[Math.floor(backwardIndex/BOARD_COLUMNS)][backwardIndex%BOARD_COLUMNS];
                if (backwardLetter === " ") {
                    this.focusSquare(backwardIndex);
                } else if (backwardLetter === ".") {
                    const new_squares = this.toggleBlackSquare(backwardIndex, squares);
                    this.focusSquare(backwardIndex)
                    this.addHistory(new_squares);
                } else {
                    squares[Math.floor(backwardIndex/BOARD_COLUMNS)][backwardIndex%BOARD_COLUMNS] = " ";
                    this.focusSquare(backwardIndex);
                    this.addHistory(squares);
                }
            } else if (squares[row][column] === '.') {
                const new_squares = this.toggleBlackSquare(index, squares)
                this.addHistory(new_squares);
            } else {
                squares[row][column] = " ";
                this.addHistory(squares);
            }

        } else if (input === " ") {
            if (squares[row][column] === " ") {
                this.focusSquare(this.getForwardIndex(index));
            } else {
                squares[row][column] = " ";
                this.focusSquare(this.getForwardIndex(index));
                this.addHistory(squares);
            }

        } else if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"].includes(input)) { // an arrow key was pressed
            e.preventDefault();
            this.handleArrowKey(input, index);

        } else if (input === "Tab") {
            this.setState({
                focusIndex: null,
                focusRow: null,
                focusCol: null
            });
        }
    }

    /**
     * Removes last element of history to undo previous change
     */
    undo() {
        const stepNumber = this.state.stepNumber;
        const history = this.state.history.slice();

        if (stepNumber > 0) {
            this.setState({
                stepNumber: stepNumber - 1,
                history: history.slice(0, history.length-1),
            })
        }
    }

    /**
     * Resets history to original state
     */
    reset() {
        const history = this.state.history.slice();
        this.setState({
            stepNumber: 0,
            history: [history[0]],
        })
    }

    /**
     * Function for handling menu button clicks
     * @param e
     */
    handleMenuClick = (e) => {
        const value = e.target.value;

        if (value==="undo") {
            this.undo();
        } else if (value==="reset") {
            this.reset();
        } else if (value==="symmetry") {
            const sym = this.state.symmetrical;
            this.setState({symmetrical: !sym});
        } else if (value==="pattern") {
            const board = patterns[Math.floor(Math.random() * patterns.length)];
            // TODO: get more patterns of other sizes
            if (board.length === BOARD_ROWS && board[0].length === BOARD_COLUMNS) {
                this.addHistory(board);
            }
            // gets random pattern https://stackoverflow.com/a/5915122
        } else {
            this.setState({panelControl: value});
        }
    }

    /**
     * Function for handling square clicks
     * @param e
     */
    handleSquareClick = (e) => {
        const index = parseInt(e.target.id);
        if (this.state.focusIndex === index) {
            const isAcross = this.state.isAcross;
            this.setState({isAcross: !isAcross})
        } else {
            this.focusSquare(index);
        }
    }

    /**
     * Returns a 2d array with true and false values used to highlight the current word
     * @returns {boolean[][]}
     */
    getHighlightedSquares(squares) {
        const index = this.state.focusIndex;
        if (index === null) return this.fill2dArray(false);
        const isAcross = this.state.isAcross;
        const [start_row, start_column] = this.getStartPosition(index, squares, isAcross);
        const [end_row, end_column] = this.getEndPosition(index, squares, isAcross);
        let highlights = this.fill2dArray(false);
        for (let row=start_row; row<= end_row; row++) {
            for (let col=start_column; col<= end_column; col++) {
                highlights[row][col] = true;
            }
        }
        highlights[this.state.focusRow][this.state.focusCol] = false;

        return highlights
    }

    /**
     * Function for filling suggestion onto grid
     * @param e
     * @param isAcross
     */
    handleSuggestionClick = (e, isAcross) => {
        e.preventDefault();
        if (e.target.className !== 'suggestion') return;
        const word = e.target.textContent;
        const squares = this.getCurrentSquares();
        const index = this.state.focusIndex;
        const new_squares = this.fillWord(index, squares, word, isAcross);
        this.addHistory(new_squares);
        this.setState({isAcross: isAcross})
        this.focusSquare(index);
    }

    /**
     * Returns a new grid that fills the across or down word that goes through the square at index
     * @param {int} index
     * @param {string[][]} squares
     * @param {string} word - word to be inserted
     * @param {boolean} isAcross
     * @returns {string[][]}
     */
    fillWord(index, squares, word, isAcross) {
        const [start_row, start_column] = this.getStartPosition(index, squares, isAcross);
        const [end_row, end_column] = this.getEndPosition(index, squares, isAcross);
        if (isAcross) {
            let word_index = 0;
            for (let i=start_column; i<= end_column; i++) {
                squares[start_row][i] = word[word_index]
                word_index = word_index + 1
            }
        } else {
            let word_index = 0;
            for (let i=start_row; i<= end_row; i++) {
                squares[i][start_column] = word[word_index]
                word_index = word_index + 1
            }
        }


        return squares
    }

    /**
     * Removes focus from square if there is a click outside of the grid
     * @param e
     */
    handleBodyClick = (e) => {
        if (!['normal-square', 'highlighted-square', 'black-square', 'suggestion', 'suggestion-box'].includes(e.target.className)) {
            if (e.target.className==="menu-button") {
                e.preventDefault();
                e.target.blur();
            } else {
                this.setState({
                    focusIndex: null,
                    focusRow: null,
                    focusCol: null
                });
            }
        }
    }

    /**
     * Calculates stats for stats panel
     * @returns {{letterCounts: {}, blackPercent: string,
     *            blackCount: int, wordLengths: {},
     *            numWords: int, letterTotal: int}}
     */
    calculateStats() {
        const allWords = this.getWordList();
        const numWords = allWords.length;
        let wordLengths = {};

        for (let i=1; i<=Math.max(BOARD_ROWS, BOARD_COLUMNS); i++) {
            wordLengths[i]=0;
        }
        for (const word of allWords) {
            const wordLength = word.length;
            wordLengths[wordLength]++;
        }

        const squares = this.getCurrentSquares();
        const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        let letterCounts = {};
        let blackCount = 0;

        for (const letter of alphabet) {
            letterCounts[letter] = 0;
        }
        for (const row of squares) {
            for (const letter of row) {
                if (letter === ".") {
                    blackCount++;
                } else if (alphabet.includes(letter)) {
                    letterCounts[letter]++;
                }
            }
        }
        const letterTotal = BOARD_ROWS*BOARD_COLUMNS - blackCount;

        const blackPercent = (100*blackCount/(BOARD_ROWS*BOARD_COLUMNS)).toFixed(2);

        return {wordLengths, numWords, letterCounts, letterTotal, blackCount, blackPercent};
    }

    render() {
        return (
            <div className="body" onMouseDown={this.handleBodyClick}>
                <div className={"menu"}>
                    <Menu
                        onClick={this.handleMenuClick}
                        status={this.state.panelControl}
                        symmetrical={this.state.symmetrical}
                    />
                </div>
                <div className={"game"}>
                    <table className="game-board" style={{width: (6*BOARD_COLUMNS).toString()+"vh"}}>
                        <Board
                            squares={this.getCurrentSquares()}
                            onClick={this.handleSquareClick}
                            labels={this.getLabelList()}
                            onKeyDown={this.handleKeyDown}
                            ref={this.state.boardRef}
                            highlightedSquares={this.getHighlightedSquares(this.getCurrentSquares())}
                        />
                    </table>
                    <div className={"panel"} ref={this.state.panelRef}>
                        <Panel
                            panelControl={this.state.panelControl}
                            currentWords={this.getCurrentWords(this.state.focusIndex)}
                            stats={this.calculateStats()}
                            onSuggestionClick={this.handleSuggestionClick}
                            currentWordLabels={this.getCurrentWordLabels(this.state.focusIndex)}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default Game